import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, Photo } from '../services/api';

const AUTO_SWIPE_MS = 4000;

/** Prefer full /original/ images over /thumbnails/. */
function toOriginalPhotoUrl(url: string): string {
  return url
    .replace('/thumbnails/', '/original/')
    .replace('_thumb.', '.')
    .replace('_thumbnail.', '.');
}

interface MatchPhotoCarouselProps {
  matchId: string;
}

const MatchPhotoCarousel: React.FC<MatchPhotoCarouselProps> = ({ matchId }) => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadPhotos = async () => {
      try {
        const response: any = await apiService.getMatchPhotos(matchId, 1, 10);
        if (cancelled) return;

        let fetchedPhotos: Photo[] = [];
        if (Array.isArray(response)) {
          fetchedPhotos = response;
        } else if (response && response.photos) {
          fetchedPhotos = response.photos;
        }

        // Prefer original size; fall back to rewriting thumbnail URLs
        const validPhotos = fetchedPhotos
          .filter((p) => Boolean(p.originalUrl || p.imageUrl))
          .slice(0, 6)
          .map((p) => ({
            ...p,
            imageUrl: p.originalUrl || toOriginalPhotoUrl(p.imageUrl),
          }));
        setPhotos(validPhotos);
      } catch (error) {
        console.error('Failed to load match photos:', error);
        if (!cancelled) setPhotos([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPhotos();
    return () => {
      cancelled = true;
    };
  }, [matchId]);

  useEffect(() => {
    if (photos.length <= 1 || paused) return undefined;

    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % photos.length);
    }, AUTO_SWIPE_MS);

    return () => window.clearInterval(timer);
  }, [photos.length, paused]);

  const goToHighlights = () => {
    navigate(`/highlights/${matchId}`);
  };

  if (loading || photos.length === 0) {
    return null;
  }

  const current = photos[index];

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '600px',
        margin: '0 auto',
        padding: '0 16px',
        boxSizing: 'border-box'
      }}
    >
      <button
        type="button"
        onClick={goToHighlights}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        aria-label="前往賽事精華"
        style={{
          display: 'block',
          width: '100%',
          padding: 0,
          border: '2px solid #e2e8f0',
          borderRadius: '12px',
          overflow: 'hidden',
          cursor: 'pointer',
          background: '#f7fafc',
          boxShadow: '0 4px 6px rgba(0,0,0,0.08)',
          textAlign: 'left',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            paddingTop: '62%',
            backgroundColor: '#edf2f7',
          }}
        >
          {photos.map((photo, photoIndex) => (
            <img
              key={photo.id}
              src={photo.imageUrl || ''}
              alt={photo.title || 'Match Photo'}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: photoIndex === index ? 1 : 0,
                transition: 'opacity 0.5s ease',
              }}
            />
          ))}
        </div>
      </button>
    </div>
  );
};

export default MatchPhotoCarousel;
